import React, { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { lightPalette } from '../../../theme';
import { QontoConnector } from '../document-management/flow-configuration/utils';
import { globalClasses } from '../utils/globalUtils';
import { CustomStepIcon } from '../document-management/flow-configuration/nodeConnection';
import { classes } from './utils';
import { getDashboardFlowsData } from '../../../services/flowConfiguration';
import { getFlowStatistics } from '../../../services/adminDashboard';

const renderLegend = (props) => {
  const { payload } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        justifyContent: 'space-around',
      }}
    >
      {payload?.map((entry, index) => (
        <Box
          key={`${index + 1}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            key={`${index + 1}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                marginRight: '8px',
                backgroundColor: entry?.color || 'black',
              }}
            />
            <Box
              sx={{
                color: lightPalette.deepAmethyst.semiDark,
              }}
            >
              {entry.value}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              marginLeft: '20px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Heebo',
                fontWeight: '500',
                color: lightPalette.deepAmethyst.main,
              }}
            >
              {entry?.payload?.value}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
renderLegend.defaultProps = {
  payload: [],
};

renderLegend.propTypes = {
  payload: PropTypes.arrayOf(),
};

const FlowConfigurationDetailsAndChart = ({
  itemName,
  selectedDashboardType,
  filteredTypes,
  getTypeByTitle,
}) => {
  const [fcChartData, setFcChartData] = useState([
    { name: 'Total Flow', value: 0 },
    { name: 'Active Flow', value: 0 },
    { name: 'Inactive Flow', value: 0 },
    { name: 'Failed Flow', value: 0 },
  ]);

  const [flows, setFlows] = useState([]);
  const [connectedNodes, setConnectedNodes] = useState([]);

  const [loading, setLoading] = useState(true);

  const COLORS = [
    'rgba(237, 227, 255, 1)',
    'rgba(210, 187, 254, 1)',
    'rgba(185, 150, 252, 1)',
    'rgba(159, 119, 235, 1)',
  ];

  const orderedConnections = (allEdges, extractBaseName) => {
    const connections = allEdges?.map((edge) => ({
      source: edge.source,
      target: edge.target,
      sourceBase: extractBaseName(edge.source),
      targetBase: extractBaseName(edge.target),
    }));

    const map = {};
    connections?.forEach((connection) => {
      map[connection.source] = connection.target;
    });

    const startNode = connections?.find(
      (connection) => !Object.values(map).includes(connection?.source),
    )?.source;

    const buildSequence = (node, sequence = []) => {
      if (node) {
        sequence.push(node);
        return buildSequence(map[node], sequence);
      }
      return sequence;
    };

    const sequence = buildSequence(startNode);
    return sequence?.map(extractBaseName);
  };

  const getAllFlow = async () => {
    const typePayloadArr =
      selectedDashboardType === 'compare'
        ? getTypeByTitle(itemName)
        : filteredTypes;
    const filterValue = { name: '', status: '', typePayloadArr };

    const res = await getDashboardFlowsData(filterValue);
    if (res) {
      res?.forEach((flow) => {
        const edges = flow?.details?.edge;
        const extractBaseName = (str) => str.split('_')[0];

        const result = orderedConnections(edges, extractBaseName);
        setConnectedNodes((prev) => ({
          ...prev,
          [flow?.id]: result,
        }));
      });
      setFlows(res);
    }
    setLoading(false);
  };

  const getCountDataFlows = async () => {
    const typePayloadArr =
      selectedDashboardType === 'compare'
        ? getTypeByTitle(itemName)
        : filteredTypes;

    const response = await getFlowStatistics(typePayloadArr);
    if (response && Object.keys(response?.data?.stages)?.length > 0) {
      const finalData = response.data.stages;
      setFcChartData([
        { name: 'Total Flow', value: finalData?.total_flows },
        { name: 'Active Flow', value: finalData?.active_flows },
        { name: 'Inactive Flow', value: finalData?.inactive_flows },
        { name: 'Failed Flow', value: finalData?.failed_flows },
      ]);
      setLoading(false);
    } else {
      setFcChartData([]);
      setLoading(true);
    }
  };

  useEffect(() => {
    getCountDataFlows();

    getAllFlow();
  }, [filteredTypes]);

  const limitedFlow = flows?.slice(0, 10);

  const tooltipFormatter = (value, name) => [value, name];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90%"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        height: '90%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          height: '30%',
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
          style={{
            display: 'flex',
            justifyContent: 'left',
          }}
        >
          <PieChart width={400} height={200}>
            <Pie
              data={fcChartData}
              innerRadius={25}
              outerRadius={35}
              dataKey="value"
            >
              {fcChartData?.map((entry, index) => (
                <Cell
                  key={`cell-${index + 1}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 1)',
                color: 'rgba(0, 0, 0, 0.6)',
                boxShadow: '0px 0px 11px 4px rgba(0, 0, 0, 0.08)',
                borderRadius: '5px',
                padding: '10px',
              }}
              labelStyle={{ display: 'none' }}
              itemStyle={{ color: 'rgba(0, 0, 0, 0.6)' }}
            />
            <Legend
              align="right"
              verticalAlign="middle"
              width="82%"
              content={renderLegend}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          height: '70%',
        }}
      >
        <TableContainer
          className={classes.fcTableMainContainer}
          component={Paper}
        >
          <Table stickyHeader className={classes.fcTable}>
            <TableBody>
              {limitedFlow?.map((flow) => (
                <TableRow key={flow?.id}>
                  <Box className={classes.fcTableRowClass}>
                    <TableCell className={classes.fcTableRowCell}>
                      <Typography
                        variant="body1"
                        className={classes.fcNameTypo}
                      >
                        {flow?.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      className={classes.fcTableRowCell}
                      sx={{ width: '70%' }}
                    >
                      <Box>
                        <Stepper
                          connector={<QontoConnector />}
                          className={classes.fcNodeStepper}
                        >
                          {connectedNodes[flow?.id]?.map((label) => (
                            <Step key={label}>
                              <StepLabel
                                StepIconComponent={() =>
                                  CustomStepIcon({
                                    icon: label,
                                  })
                                }
                              />
                            </Step>
                          ))}
                        </Stepper>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.fcTableRowCell}>
                      <Box
                        className={`${globalClasses.displayFlex} ${globalClasses.displayAlignItem}`}
                      >
                        <Chip
                          label={
                            <Box display="flex" alignItems="center">
                              <Box
                                component="span"
                                width={8}
                                height={8}
                                borderRadius="50%"
                                mr={1}
                                bgcolor={
                                  flow.status === 'Running'
                                    ? lightPalette.jadeGreen.main
                                    : lightPalette.colorRed.main
                                }
                              />
                              <Typography
                                variant="body1"
                                color={
                                  flow.status === 'Running'
                                    ? lightPalette.colorGreen.main
                                    : lightPalette.colorRed.main
                                }
                              >
                                {flow?.status}
                              </Typography>
                            </Box>
                          }
                          className={`${classes.fcStatusChipPadding} ${flow?.status === 'Running' ? classes.fcRunningChip : classes.fcStopChip}`}
                        />
                      </Box>
                    </TableCell>
                  </Box>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

FlowConfigurationDetailsAndChart.defaultProps = {
  itemName: '',
  filteredTypes: [],
  getTypeByTitle: null,
};

FlowConfigurationDetailsAndChart.propTypes = {
  itemName: PropTypes.string,
  selectedDashboardType: PropTypes.string.isRequired,
  filteredTypes: PropTypes.arrayOf(),
  getTypeByTitle: PropTypes.func,
};

export default FlowConfigurationDetailsAndChart;
